import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, Send, Trash2, BookOpen, ChevronDown, ChevronUp, Mic, MicOff } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { usePageContext } from '@/hooks/usePageContext';
import { sendAskNewAiMessage, sendAskNewAiMessageStreaming, type ProgressEvent } from '@/services/askNewAiService';

type MessageRole = 'user' | 'assistant';

interface DisambiguationOption {
  id: string;
  label: string;
}

interface Disambiguation {
  type: 'location' | 'vendor' | 'contract' | 'other';
  question: string;
  options: DisambiguationOption[];
}

interface ChatMessage {
  role: MessageRole;
  content: string;
  debug?: {
    route?: any;
    plan?: any;
    bag?: Record<string, any>;
  };
  disambiguation?: Disambiguation;
  timestamp?: number;
  isPending?: boolean;
  progress?: ProgressEvent;
}

const TABS = [
  { id: 'chat', label: 'Chat' },
  { id: 'help', label: 'Help' },
];

const deriveModuleFromPath = (pathname: string): string | undefined => {
  if (!pathname) return undefined;
  if (pathname.includes('polic')) return 'policies';
  if (pathname.includes('agreement')) return 'agreements';
  if (pathname.includes('checklist')) return 'checklists';
  if (pathname.includes('task')) return 'tasks';
  if (pathname.includes('form')) return 'forms';
  if (pathname.includes('evidence')) return 'evidence';
  return undefined;
};

const DEFAULT_GREETING: ChatMessage = {
  role: 'assistant',
  content: 'Hi! I can help with tasks, policies, contracts, and setup questions. What would you like to know?',
  timestamp: Date.now(),
};

const HISTORY_LIMIT = 60;

const AskNewAiPanel: React.FC = () => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const pageContext = usePageContext();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<'chat' | 'help'>('chat');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [feedbackStates, setFeedbackStates] = useState<Record<number, { sent: boolean; submitting: boolean }>>({});
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [supportMessage, setSupportMessage] = useState('');
  const [supportSubmitting, setSupportSubmitting] = useState(false);
  const [supportSuccess, setSupportSuccess] = useState<string | null>(null);
  const [pendingIntent, setPendingIntent] = useState<'app_help' | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  const [expandedDebug, setExpandedDebug] = useState<Record<number, boolean>>({});
  
  // Voice input state
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [isSpeechSupported, setIsSpeechSupported] = useState(false);
  const [micPermissionStatus, setMicPermissionStatus] = useState<'unknown' | 'granted' | 'denied' | 'prompt'>('unknown');
  const recognitionRef = useRef<any>(null);

  const historyKey = useMemo(
    () => (user?.id ? `complin-asknewai:${user.id}` : 'complin-asknewai:guest'),
    [user?.id],
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeTab]);

  useEffect(() => {
    if (!historyKey) return;
    try {
      const raw = localStorage.getItem(historyKey);
      if (raw) {
        const parsed = JSON.parse(raw) as ChatMessage[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(
            parsed.map((m) => ({
              ...m,
              isPending: false,
              timestamp: m.timestamp ?? Date.now(),
            })),
          );
          setHasLoadedHistory(true);
          return;
        }
      }
    } catch (err) {
      console.warn('AskNewAI failed to load chat history', err);
    }
    setMessages([DEFAULT_GREETING]);
    setHasLoadedHistory(true);
  }, [historyKey]);

  useEffect(() => {
    if (!hasLoadedHistory || !historyKey || messages.length === 0) return;
    try {
      const sanitized = messages
        .filter((m) => !m.isPending)
        .map((m) => ({
          ...m,
          timestamp: m.timestamp ?? Date.now(),
        }))
        .slice(-HISTORY_LIMIT);
      localStorage.setItem(historyKey, JSON.stringify(sanitized));
    } catch (err) {
      console.warn('AskNewAI failed to persist history', err);
    }
  }, [messages, historyKey, hasLoadedHistory]);

  // Check microphone permissions
  useEffect(() => {
    if (typeof navigator === 'undefined') {
      setMicPermissionStatus('unknown');
      return;
    }

    // Try to query permissions, but handle browsers that don't support it
    if (navigator.permissions && navigator.permissions.query) {
      // Some browsers may not support microphone permission query
      const queryPromise = navigator.permissions.query({ name: 'microphone' as PermissionName })
        .then((result) => {
          setMicPermissionStatus(result.state as any);
          // Listen for permission changes
          result.onchange = () => {
            setMicPermissionStatus(result.state as any);
          };
        })
        .catch(() => {
          // Permissions API might not support microphone query on all browsers
          // This is okay, we'll rely on getUserMedia error handling
          setMicPermissionStatus('unknown');
        });
      
      // Don't wait for this, it's just for initial status
      void queryPromise;
    } else {
      // Permissions API not supported, will rely on error handling
      setMicPermissionStatus('unknown');
    }
  }, []);

  // Initialize speech recognition
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    if (SpeechRecognition) {
      setIsSpeechSupported(true);
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
        setInterimTranscript('');
      };

      recognition.onresult = (event: any) => {
        let interim = '';
        let final = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            final += transcript + ' ';
          } else {
            interim += transcript;
          }
        }

        if (final) {
          // Append final transcript to input
          setInput((prev) => {
            const newValue = (prev + ' ' + final).trim();
            return newValue;
          });
          setInterimTranscript('');
        } else if (interim) {
          // Show interim results
          setInterimTranscript(interim);
        }
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        setInterimTranscript('');
        
        // Handle specific errors
        if (event.error === 'no-speech') {
          // User stopped speaking - this is normal, just stop listening
        } else if (event.error === 'audio-capture') {
          setMicPermissionStatus('denied');
          alert('Microphone not found. Please check your microphone is connected and try again.');
        } else if (event.error === 'not-allowed') {
          setMicPermissionStatus('denied');
          // Don't show alert here, we'll show a better UI message
          console.warn('Microphone permission denied');
        } else {
          console.warn('Speech recognition error:', event.error);
        }
      };

      recognition.onend = () => {
        setIsListening(false);
        // Finalize any remaining interim transcript
        if (interimTranscript) {
          setInput((prev) => (prev + ' ' + interimTranscript).trim());
          setInterimTranscript('');
        }
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (e) {
          // Ignore errors when stopping
        }
        recognitionRef.current = null;
      }
    };
  }, []);

  const quickPrompts = useMemo(
    () => [
      {
        label: 'Overdue high-risk tasks',
        text: 'Which high-risk tasks are overdue this week and what penalties apply?',
        intent: null,
      },
      {
        label: 'Contracts renewing soon',
        text: 'Show contracts renewing next month and recommend next steps.',
        intent: null,
      },
      {
        label: 'Set up organization',
        text: 'Set up my first organization',
        intent: 'app_help' as const,
      },
      {
        label: 'Evidence types',
        text: 'How do required evidence types work?',
        intent: 'app_help' as const,
      },
    ],
    [],
  );

  const sendMessage = async (
    rawText: string,
    options?: { forceIntent?: 'app_help' | null; selectedLocationId?: string },
  ) => {
    const trimmed = rawText.trim();
    if (!trimmed || !user?.id) return;

    setIsSending(true);
    setInput('');

    const userMessage: ChatMessage = {
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    };
    setMessages((prev) => [...prev, userMessage]);

    const historyTranscript = [...messages.filter((m) => !m.isPending), userMessage]
      .slice(-HISTORY_LIMIT)
      .map(({ role, content }) => ({ role, content }));

    // Create pending message for progress updates
    const pendingMessageId = Date.now();
    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        content: 'Starting AI analysis...',
        isPending: true,
        progress: {
          step: 'starting',
          message: 'Starting AI analysis...',
          timestamp: Date.now(),
        },
        timestamp: pendingMessageId,
      },
    ]);

    try {
      const contextModule = deriveModuleFromPath(location.pathname);
      const context: any = {
        userId: user.id,
        organizationId: currentOrganization?.id,
        pathname: location.pathname,
        pageContext,
        currentModule: contextModule,
        forceAppHelp: options?.forceIntent === 'app_help',
      };

      // If a location was selected from disambiguation, include it in context
      if (options?.selectedLocationId) {
        context.selectedLocationId = options.selectedLocationId;
        context.locationIds = [options.selectedLocationId];
      }

      // Use streaming with progress updates
      const resp = await sendAskNewAiMessageStreaming(
        {
          messages: historyTranscript,
          context,
        },
        (progressEvent) => {
          // Update the pending message with progress
          setMessages((prev) => {
            const index = prev.findIndex((m) => m.timestamp === pendingMessageId && m.isPending);
            if (index === -1) return prev;
            
            const updated = [...prev];
            updated[index] = {
              ...updated[index],
              content: progressEvent.message,
              progress: progressEvent,
            };
            return updated;
          });
        }
      );

      // Replace pending message with final response
      setMessages((prev) => {
        const withoutPending = prev.filter((m) => !(m.isPending && m.timestamp === pendingMessageId));
        return [
          ...withoutPending,
          {
            role: 'assistant',
            content: resp.reply,
            debug: {
              route: resp.route,
              plan: resp.plan,
              bag: resp.bag,
            },
            disambiguation: resp.disambiguation,
            timestamp: Date.now(),
          },
        ];
      });
    } catch (err: any) {
      console.error('AskNewAI send error', err);
      
      // Provide user-friendly error messages
      let errorMessage = 'Sorry, I encountered an error while processing your request.';
      if (err?.message) {
        if (err.message.includes('connection was interrupted') || err.message.includes('network')) {
          errorMessage = 'The connection was interrupted. This might be due to a network issue. Please try again.';
        } else if (err.message.includes('timeout')) {
          errorMessage = 'The request took too long to process. Please try again with a simpler question.';
        } else if (err.message.includes('HTTP')) {
          errorMessage = 'There was a problem connecting to the server. Please check your connection and try again.';
        } else {
          // For other errors, show a more helpful message
          errorMessage = `I couldn't process your request: ${err.message}`;
        }
      }
      
      setMessages((prev) => {
        const withoutPending = prev.filter((m) => !m.isPending);
        return [
          ...withoutPending,
          {
            role: 'assistant',
            content: errorMessage,
            timestamp: Date.now(),
          },
        ];
      });
    } finally {
      setIsSending(false);
      setPendingIntent(null);
    }
  };

  // Render text with markdown-style links converted to clickable anchor tags
  // Progress indicator component
  const ProgressIndicator = ({ progress }: { progress?: ProgressEvent }) => {
    if (!progress) return null;
    
    const steps: Array<{ step: ProgressEvent['step']; label: string }> = [
      { step: 'starting', label: 'Starting' },
      { step: 'routing', label: 'Understanding' },
      { step: 'planning', label: 'Planning' },
      { step: 'retrieving', label: 'Retrieving' },
      { step: 'composing', label: 'Composing' },
    ];
    
    const currentStepIndex = steps.findIndex(s => s.step === progress.step);
    const activeSteps = steps.slice(0, currentStepIndex + 1);
    
    return (
      <div className="mt-2 pt-2 border-t border-gray-200">
        <div className="flex items-center gap-1.5 flex-wrap">
          {steps.map((step, idx) => {
            const isActive = idx <= currentStepIndex;
            const isCurrent = idx === currentStepIndex;
            return (
              <div key={step.step} className="flex items-center gap-1">
                <div
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    isActive
                      ? isCurrent
                        ? 'bg-indigo-600 animate-pulse'
                        : 'bg-indigo-400'
                      : 'bg-gray-300'
                  }`}
                />
                <span
                  className={`text-[9px] transition-colors ${
                    isActive ? 'text-indigo-600 font-medium' : 'text-gray-400'
                  }`}
                >
                  {step.label}
                </span>
                {idx < steps.length - 1 && (
                  <span className="text-gray-300 mx-0.5">•</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const LoadingDots = () => (
    <span className="inline-flex items-center gap-1 ml-1.5">
      <span className="w-1.5 h-1.5 bg-current rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
      <span className="w-1.5 h-1.5 bg-current rounded-full animate-pulse" style={{ animationDelay: '200ms' }} />
      <span className="w-1.5 h-1.5 bg-current rounded-full animate-pulse" style={{ animationDelay: '400ms' }} />
    </span>
  );

  const renderMessageContent = (content: string) => {
    return (
      <div className="prose prose-sm max-w-none prose-headings:mt-2 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
          // Style links
          a: ({ node, ...props }) => (
            <a
              {...props}
              target="_blank"
              rel="noopener noreferrer"
              className="text-indigo-600 hover:text-indigo-800 underline font-medium break-all"
            />
          ),
          // Style tables
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-2">
              <table
                {...props}
                className="min-w-full border-collapse border border-gray-300 text-xs"
              />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead {...props} className="bg-gray-100" />
          ),
          th: ({ node, ...props }) => (
            <th
              {...props}
              className="border border-gray-300 px-2 py-1 text-left font-semibold"
            />
          ),
          td: ({ node, ...props }) => (
            <td
              {...props}
              className="border border-gray-300 px-2 py-1"
            />
          ),
          // Style code blocks
          code: ({ node, inline, ...props }: any) => (
            <code
              {...props}
              className={
                inline
                  ? 'bg-gray-100 px-1 py-0.5 rounded text-xs font-mono'
                  : 'block bg-gray-100 p-2 rounded text-xs font-mono overflow-x-auto my-2'
              }
            />
          ),
          // Style lists
          ul: ({ node, ...props }) => (
            <ul {...props} className="list-disc list-inside space-y-0.5 my-1" />
          ),
          ol: ({ node, ...props }) => (
            <ol {...props} className="list-decimal list-inside space-y-0.5 my-1" />
          ),
          // Style headers
          h2: ({ node, ...props }) => (
            <h2 {...props} className="text-base font-semibold mt-3 mb-1.5" />
          ),
          h3: ({ node, ...props }) => (
            <h3 {...props} className="text-sm font-semibold mt-2 mb-1" />
          ),
          // Style paragraphs
          p: ({ node, ...props }) => (
            <p {...props} className="my-1" />
          ),
          // Style strong/bold
          strong: ({ node, ...props }) => (
            <strong {...props} className="font-semibold text-gray-900" />
          ),
          // Style emphasis/italic
          em: ({ node, ...props }) => (
            <em {...props} className="italic" />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
      </div>
    );
  };

  const renderDisambiguationOptions = (disambiguation: Disambiguation, messageIndex: number) => {
    if (!disambiguation.options || disambiguation.options.length === 0) return null;

    const handleSelection = (option: DisambiguationOption) => {
      // Re-send the original question with the selected location ID in context
      const questionWithLocation = `${disambiguation.question} for ${option.label}`;
      void sendMessage(questionWithLocation, { selectedLocationId: option.id });
    };

    return (
      <div className="mt-2 space-y-1.5">
        <div className="text-[10px] text-gray-600 font-medium">Select one:</div>
        <div className="flex flex-wrap gap-1.5">
          {disambiguation.options.map((option) => (
            <button
              key={option.id}
              onClick={() => handleSelection(option)}
              className="px-2.5 py-1.5 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 rounded text-xs font-medium transition-colors"
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderFeedbackButton = (message: ChatMessage, index: number) => {
    if (message.role !== 'assistant' || !message.content) return null;
    
    const state = feedbackStates[index] || { sent: false, submitting: false };

    const handleFeedback = async () => {
      if (state.sent || state.submitting) return;
      
      setFeedbackStates((prev) => ({ ...prev, [index]: { sent: false, submitting: true } }));
      try {
        // Build debug information
        const debugInfo = message.debug
          ? `\n\n--- Debug Information ---\n\nRoute:\n${JSON.stringify(message.debug.route, null, 2)}\n\nPlan:\n${JSON.stringify(message.debug.plan, null, 2)}\n\nTools:\n${JSON.stringify(message.debug.bag, null, 2)}`
          : '\n\n--- Debug Information ---\n\nNo debug information available.';

        const resp = await fetch('https://us-central1-lemur-476302.cloudfunctions.net/contactSupport', {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            name: user?.name,
            email: user?.email,
            message: `AI Chat Feedback - User didn't like the answer\n\nQuestion: ${messages[index - 1]?.content || 'N/A'}\n\nAI Response: ${message.content}\n\nPathname: ${location.pathname}${debugInfo}`,
            pathname: location.pathname,
          }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        setFeedbackStates((prev) => ({ ...prev, [index]: { sent: true, submitting: false } }));
      } catch (err: any) {
        console.error('Feedback submission error:', err);
        alert('Failed to send feedback. Please try again.');
        setFeedbackStates((prev) => ({ ...prev, [index]: { sent: false, submitting: false } }));
      }
    };

    return (
      <div className="mt-1.5">
        <button
          type="button"
          onClick={handleFeedback}
          disabled={state.submitting || state.sent}
          className="text-[10px] text-gray-500 hover:text-indigo-600 transition-colors disabled:opacity-50"
        >
          {state.sent ? '✓ Feedback sent' : state.submitting ? 'Sending...' : "I didn't like the answer. Share feedback."}
        </button>
      </div>
    );
  };

  const renderDebugInfo = (message: ChatMessage, index: number) => {
    // Only show debug info on localhost
    const isLocalhost = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || 
       window.location.hostname === '127.0.0.1' ||
       window.location.hostname.startsWith('192.168.') ||
       window.location.hostname.startsWith('10.'));
    
    if (!isLocalhost || !message.debug || message.role !== 'assistant') return null;
    
    const isExpanded = expandedDebug[index] || false;
    const toggleDebug = () => {
      setExpandedDebug((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    return (
      <div className="mt-2 border-t border-gray-200 pt-2">
        <button
          type="button"
          onClick={toggleDebug}
          className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-indigo-600 transition-colors w-full"
        >
          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          <span>Debug Information</span>
        </button>
        {isExpanded && (
          <div className="mt-2 space-y-2 text-[10px]">
            {message.debug.route && (
              <div>
                <div className="font-semibold text-gray-700 mb-1">Route:</div>
                <pre className="bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto text-[9px] font-mono">
                  {JSON.stringify(message.debug.route, null, 2)}
                </pre>
              </div>
            )}
            {message.debug.plan && (
              <div>
                <div className="font-semibold text-gray-700 mb-1">Plan:</div>
                <pre className="bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto text-[9px] font-mono">
                  {JSON.stringify(message.debug.plan, null, 2)}
                </pre>
              </div>
            )}
            {message.debug.bag && (
              <div>
                <div className="font-semibold text-gray-700 mb-1">Tools/Bag:</div>
                <pre className="bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto text-[9px] font-mono">
                  {JSON.stringify(message.debug.bag, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };


  const clearConversation = () => {
    if (!historyKey) return;
    try {
      localStorage.removeItem(historyKey);
    } catch (err) {
      console.warn('AskNewAI failed to clear history', err);
    }
    setMessages([DEFAULT_GREETING]);
    setFeedbackStates({});
  };

  const requestMicrophonePermission = async (): Promise<boolean> => {
    try {
      // Request microphone access explicitly
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach(track => track.stop());
      setMicPermissionStatus('granted');
      return true;
    } catch (err: any) {
      console.error('Microphone permission error:', err);
      setMicPermissionStatus('denied');
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        // Show helpful instructions
        const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
        const isEdge = /Edg/.test(navigator.userAgent);
        const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
        
        let instructions = 'Microphone access is blocked.\n\n';
        if (isChrome || isEdge) {
          instructions += 'To enable:\n1. Click the lock icon (🔒) in the address bar\n2. Set "Microphone" to "Allow"\n3. Refresh the page\n\nOr go to: Settings → Privacy and security → Site settings → Microphone';
        } else if (isSafari) {
          instructions += 'To enable:\n1. Safari → Preferences → Websites → Microphone\n2. Allow access for this site\n3. Refresh the page';
        } else {
          instructions += 'To enable:\n1. Check your browser settings for microphone permissions\n2. Allow access for this site\n3. Refresh the page';
        }
        
        alert(instructions);
      } else if (err.name === 'NotFoundError') {
        alert('No microphone found. Please connect a microphone and try again.');
      } else {
        alert(`Failed to access microphone: ${err.message || 'Unknown error'}`);
      }
      return false;
    }
  };

  const toggleListening = async () => {
    if (!isSpeechSupported || !recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome or Edge.');
      return;
    }

    if (isListening) {
      try {
        recognitionRef.current.stop();
      } catch (err) {
        console.error('Error stopping recognition:', err);
      }
      // State will be updated in onend handler
    } else {
      // Check and request permission first
      if (micPermissionStatus === 'denied' || micPermissionStatus === 'unknown') {
        const hasPermission = await requestMicrophonePermission();
        if (!hasPermission) {
          return;
        }
      }

      try {
        // Clear interim transcript when starting new recording
        setInterimTranscript('');
        recognitionRef.current.start();
      } catch (err: any) {
        console.error('Error starting recognition:', err);
        if (err.message?.includes('already started')) {
          // Recognition is already running, just update state
          setIsListening(true);
        } else if (err.message?.includes('not-allowed') || err.name === 'NotAllowedError') {
          // Permission might have been denied, try requesting again
          const hasPermission = await requestMicrophonePermission();
          if (hasPermission) {
            try {
              recognitionRef.current.start();
            } catch (retryErr) {
              console.error('Error after permission grant:', retryErr);
            }
          }
        } else {
          alert('Failed to start voice input. Please check your microphone is working and try again.');
        }
      }
    }
  };

  // Stop listening when switching tabs
  useEffect(() => {
    if (activeTab !== 'chat' && isListening && recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore errors
      }
    }
  }, [activeTab, isListening]);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Tabs */}
      <div className="flex border-b text-xs px-3 pt-1.5 flex-shrink-0">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`px-2.5 py-1.5 -mb-px border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'border-indigo-600 text-indigo-700 font-semibold'
                : 'border-transparent text-gray-500 hover:text-indigo-600'
            }`}
          >
            {tab.label}
          </button>
        ))}
        {activeTab === 'chat' && (
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={clearConversation}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
              type="button"
            >
              <Trash2 className="w-3 h-3" />
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {activeTab === 'chat' ? (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-2 space-y-2 bg-gray-50">
            {messages.map((message, idx) => (
              <div key={idx} className={`max-w-[90%] ${message.role === 'user' ? 'ml-auto text-right' : 'text-left'}`}>
                <div
                  className={`text-[10px] uppercase tracking-wide mb-0.5 flex items-center gap-1 ${
                    message.role === 'user' ? 'text-indigo-600' : 'text-gray-500'
                  }`}
                >
                  {message.role === 'user' ? (
                    'You'
                  ) : (
                    <>
                      <Sparkles className="w-3 h-3 text-indigo-500" />
                      <span>AI</span>
                    </>
                  )}
                </div>
                <div
                  className={`rounded-lg px-3 py-1.5 shadow-sm whitespace-pre-wrap leading-snug text-sm break-words overflow-wrap-anywhere ${
                    message.role === 'user'
                      ? 'bg-indigo-600 text-white rounded-br-sm'
                      : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm'
                  }`}
                >
                  {message.isPending ? (
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 mt-0.5">
                        <Sparkles className="w-5 h-5 text-orange-500 animate-spin" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-1">
                          <div className="flex-1 min-w-0 break-words overflow-wrap-anywhere">
                            <p className="text-sm font-medium text-gray-700">{message.progress?.message || message.content}</p>
                          </div>
                        </div>
                        {message.progress && (
                          <ProgressIndicator progress={message.progress} />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-1.5">
                      {message.role === 'assistant' && (
                        <div className="flex-shrink-0 mt-0.5">
                          <Sparkles className="w-4 h-4 text-indigo-500" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0 break-words overflow-wrap-anywhere">
                        {message.role === 'assistant' ? renderMessageContent(message.content) : message.content}
                      </div>
                    </div>
                  )}
                  {message.disambiguation && renderDisambiguationOptions(message.disambiguation, idx)}
                  {renderFeedbackButton(message, idx)}
                  {renderDebugInfo(message, idx)}
                </div>
                {message.timestamp && (
                  <div className="text-[9px] text-gray-400 mt-0.5">
                    {new Date(message.timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                  </div>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="p-2.5 border-t bg-white flex-shrink-0">
          <div className="flex flex-wrap gap-1 mb-2">
            {quickPrompts.map((prompt) => (
              <button
                key={prompt.label}
                onClick={() => {
                  setPendingIntent(prompt.intent);
                  void sendMessage(prompt.text, { forceIntent: prompt.intent });
                }}
                className="text-[10px] px-2 py-0.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {prompt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5">
            <div className="flex-1 relative">
              <textarea
                value={input + (interimTranscript ? ' ' + interimTranscript : '')}
                onChange={(e) => {
                  const newValue = e.target.value;
                  // Remove interim transcript from the value if it's there
                  if (interimTranscript && newValue.endsWith(interimTranscript)) {
                    setInput(newValue.slice(0, -interimTranscript.length).trim());
                    setInterimTranscript('');
                  } else {
                    setInput(newValue);
                    setInterimTranscript('');
                  }
                  if (pendingIntent) {
                    setPendingIntent(null);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    const textToSend = input + (interimTranscript ? ' ' + interimTranscript : '');
                    if (textToSend.trim()) {
                      void sendMessage(textToSend.trim(), { forceIntent: pendingIntent });
                      setInterimTranscript('');
                    }
                  }
                }}
                placeholder={user?.id ? (isListening ? 'Listening...' : 'Ask something or click mic to speak...') : 'Log in to chat'}
                className="flex-1 w-full border border-gray-300 rounded px-2.5 py-1.5 text-xs resize-none h-9 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 transition pr-2"
                disabled={!user?.id}
              />
              {isListening && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 pointer-events-none">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  <span className="text-[10px] text-red-600 font-medium">Listening</span>
                </div>
              )}
            </div>
            {isSpeechSupported && user?.id && (
              <button
                onClick={toggleListening}
                disabled={isSending}
                className={`px-2.5 py-1.5 rounded text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center min-w-[36px] h-9 transition-colors relative ${
                  isListening
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : micPermissionStatus === 'denied'
                    ? 'bg-orange-200 hover:bg-orange-300 text-orange-700'
                    : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                }`}
                title={
                  isListening
                    ? 'Stop listening'
                    : micPermissionStatus === 'denied'
                    ? 'Microphone permission denied. Click to enable.'
                    : 'Start voice input'
                }
                type="button"
              >
                {isListening ? (
                  <MicOff className="w-3.5 h-3.5" />
                ) : micPermissionStatus === 'denied' ? (
                  <>
                    <Mic className="w-3.5 h-3.5" />
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-orange-600 rounded-full border border-white"></span>
                  </>
                ) : (
                  <Mic className="w-3.5 h-3.5" />
                )}
              </button>
            )}
            <button
              onClick={() => {
                const textToSend = input + (interimTranscript ? ' ' + interimTranscript : '');
                if (textToSend.trim()) {
                  void sendMessage(textToSend.trim(), { forceIntent: pendingIntent });
                }
                // Stop listening if active
                if (isListening && recognitionRef.current) {
                  try {
                    recognitionRef.current.stop();
                  } catch (e) {
                    // Ignore errors
                  }
                }
                // Clear interim transcript
                setInterimTranscript('');
              }}
              disabled={isSending || (!input.trim() && !interimTranscript.trim()) || !user?.id}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1.5 rounded text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center min-w-[36px] h-9"
              type="button"
            >
              {isSending ? '...' : <Send className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-2.5 space-y-2.5 bg-gray-50 text-xs min-h-0">
          <div className="flex items-center gap-1.5 font-medium text-gray-800">
            <BookOpen className="w-3.5 h-3.5" /> Quick Help
          </div>
          <div className="text-gray-700 text-xs">New here? Start with the Getting Started guide.</div>
          <div className="flex gap-1.5 flex-wrap">
            <Link
              to="/help/getting-started"
              className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-[10px] px-2 py-1 rounded inline-block transition-colors"
            >
              Getting Started
            </Link>
            <Link
              to="/evidence-vault"
              className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 text-[10px] px-2 py-1 rounded inline-block transition-colors"
            >
              Evidence Vault
            </Link>
          </div>

          <div className="pt-2 border-t border-gray-200">
            <div className="font-medium text-gray-800 mb-1 text-xs">Contextual tips</div>
            <ul className="list-disc ml-4 text-gray-700 space-y-0.5 text-xs">
              <li>
                Page: <span className="font-mono text-[10px]">{location.pathname}</span>
              </li>
              <li>Ask AI about this page.</li>
              <li>Need documents? Try Evidence Vault.</li>
            </ul>
          </div>

          <div className="pt-2 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="font-medium text-gray-800 text-xs">Need help?</div>
              <button
                onClick={() => {
                  setShowSupportForm((s) => !s);
                  setSupportSuccess(null);
                }}
                className="bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] px-2 py-1 rounded transition-colors"
              >
                {showSupportForm ? 'Close' : 'Support'}
              </button>
            </div>
            {showSupportForm && (
              <form
                className="mt-2 space-y-1.5"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setSupportSubmitting(true);
                  setSupportSuccess(null);
                  try {
                    const resp = await fetch('https://us-central1-lemur-476302.cloudfunctions.net/contactSupport', {
                      method: 'POST',
                      mode: 'cors',
                      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                      body: JSON.stringify({
                        name: user?.name,
                        email: user?.email,
                        message: supportMessage,
                        pathname: location.pathname,
                      }),
                    });
                    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                    setSupportSuccess('Thanks! Message sent.');
                    setSupportMessage('');
                  } catch (err: any) {
                    setSupportSuccess(`Failed: ${err?.message || 'Unknown error'}`);
                  } finally {
                    setSupportSubmitting(false);
                  }
                }}
              >
                {user && (
                  <div className="text-[10px] text-gray-600">
                    As <span className="font-medium">{user.name}</span>
                  </div>
                )}
                <div>
                  <label className="block text-[10px] text-gray-600 mb-0.5">Message</label>
                  <textarea
                    value={supportMessage}
                    onChange={(e) => setSupportMessage(e.target.value)}
                    rows={2}
                    className="w-full border border-gray-300 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                </div>
                {supportSuccess && (
                  <div
                    className={`text-[10px] ${
                      supportSuccess.startsWith('Thanks') ? 'text-green-700' : 'text-red-700'
                    }`}
                  >
                    {supportSuccess}
                  </div>
                )}
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={supportSubmitting}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-[10px] px-2 py-1 rounded disabled:opacity-50 transition-colors"
                  >
                    {supportSubmitting ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AskNewAiPanel;


